import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tag, ArrowLeft, Search, Loader2, Clock, Copy, Check, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BackToSearch } from '../components/BackToSearch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

export default function DirectoryDealsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/directory/coupons?limit=100`);
      setCoupons(await res.json());
    } catch (error) {
      console.error('Failed to load coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCoupons = coupons.filter(c => 
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    c.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0d1320]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/directory" className="flex items-center gap-3">
              <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-10 h-10 rounded-lg" />
              <div className="hidden sm:block">
                <span className="font-semibold text-white text-[15px]">AI Horse-Powered</span>
                <p className="text-[11px] text-gray-500">Deals & Coupons</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              <div className="hidden md:flex items-center gap-5">
                <Link to="/directory" className="text-gray-400 hover:text-white text-sm transition-colors">Directory</Link>
                <Link to="/directory/jobs" className="text-gray-400 hover:text-white text-sm transition-colors">Jobs</Link>
                <Link to="/directory/classifieds" className="text-gray-400 hover:text-white text-sm transition-colors">Classifieds</Link>
              </div>
              <Link to="/register">
                <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white border-0">
                  List Your Deal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-14 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/20 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-emerald-500/5 blur-[100px] rounded-full" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          {/* Back to Search - shows if user came from search */}
          <BackToSearch className="mb-4" />
          
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mx-auto mb-6">
              <Percent className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              Exclusive deals & coupons
            </h1>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Save money with exclusive offers from local businesses in the AI Horse-Powered network.
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search deals..."
                  className="w-full pl-14 pr-6 py-4 text-[16px] bg-white rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 shadow-xl shadow-black/20"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 lg:px-6 pb-20">
        {/* Back Link */}
        <Link to="/directory" className="inline-flex items-center gap-2 text-gray-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </Link>

        {/* Stats */}
        <div className="flex items-center gap-6 mb-8">
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-400">{coupons.length}</p>
            <p className="text-xs text-gray-500">Active Deals</p>
          </div>
        </div>

        {/* Coupons Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Tag className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Deals Found</h3>
            <p className="text-gray-500">Check back soon for new offers!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCoupons.map((coupon) => (
              <motion.div
                key={coupon.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all"
              >
                {/* Discount Badge */}
                <div className="bg-gradient-to-r from-emerald-500 to-green-500 px-4 py-2 text-center">
                  <span className="text-white font-bold text-lg">
                    {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `$${coupon.discount_value} OFF`}
                  </span>
                </div>
                
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 text-[15px] mb-1">{coupon.title}</h3>
                  <p className="text-sm text-gray-500 mb-3">{coupon.business_name}</p>
                  
                  {coupon.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{coupon.description}</p>
                  )}
                  
                  {coupon.code && (
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-emerald-600 text-sm font-mono text-center">
                        {coupon.code}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyCode(coupon.code)}
                        className="border-gray-200 text-gray-600 hover:bg-gray-50"
                      >
                        {copiedCode === coupon.code ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {coupon.expiry_date && (
                    <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Expires {new Date(coupon.expiry_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-10 px-4 bg-[#070b12] border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-8 h-8 rounded-lg" />
            <span className="font-semibold text-white text-sm">AI Horse-Powered Deals</span>
          </div>
          <p className="text-sm text-gray-600">© {new Date().getFullYear()} AI Horse-Powered Technology</p>
        </div>
      </footer>
    </div>
  );
}
